import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { TagType, TagScope } from './TagsManagement';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface TagTypeFormProps {
  tagType: TagType | null;
  onSave: (tagType: Omit<TagType, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function TagTypeForm({
  tagType,
  onSave,
  onCancel,
  locale
}: TagTypeFormProps) {
  const [formData, setFormData] = useState<Omit<TagType, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    scope: 'Global',
    orgUnitId: undefined,
    orgUnitName: undefined,
    orgUnitPath: undefined,
    isRestricted: false,
    colourPalette: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444']
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newColor, setNewColor] = useState('#6366F1');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  // Initialize form data when editing a tag type
  useEffect(() => {
    if (tagType) {
      setFormData({
        name: tagType.name,
        description: tagType.description,
        scope: tagType.scope,
        orgUnitId: tagType.orgUnitId,
        orgUnitName: tagType.orgUnitName,
        orgUnitPath: tagType.orgUnitPath,
        isRestricted: tagType.isRestricted,
        colourPalette: tagType.colourPalette || []
      });
    }
  }, [tagType]);
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scope') {
      // Reset OrgUnit fields when scope changes to Global
      setFormData({
        ...formData,
        scope: value as TagScope,
        ...(value === 'Global' ? {
          orgUnitId: undefined,
          orgUnitName: undefined,
          orgUnitPath: undefined
        } : {})
      });
      setSelectedOrgUnit(null);
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle OrgUnit selection
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setFormData({
      ...formData,
      orgUnitId: orgUnitId || undefined,
      orgUnitName: orgUnit?.name || undefined,
      orgUnitPath: orgUnit?.path || undefined
    });
    setSelectedOrgUnit(orgUnit);
  };
  // Handle adding a color to the palette
  const handleAddColor = () => {
    if (newColor && (!formData.colourPalette || !formData.colourPalette.includes(newColor))) {
      setFormData({
        ...formData,
        colourPalette: [...(formData.colourPalette || []), newColor]
      });
    }
  };
  // Handle removing a color from the palette
  const handleRemoveColor = (colorToRemove: string) => {
    setFormData({
      ...formData,
      colourPalette: formData.colourPalette?.filter(color => color !== colorToRemove) || []
    });
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tag type name is required';
    }
    if (formData.scope === 'OrgUnit' && !formData.orgUnitId) {
      newErrors.orgUnitId = 'Organisation unit must be selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-20640">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-20641">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20642">
          {tagType ? 'Edit Tag Type' : 'Create Tag Type'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-20643">
          {tagType ? 'Update the details of an existing tag type' : 'Define a new tag type to categorize tags'}
        </p>
      </div>

      <form onSubmit={handleSubmit} data-id="element-20644">
        <div className="px-4 py-5 sm:p-6" data-id="element-20645">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-20646">
            {/* Scope */}
            <div className="sm:col-span-3" data-id="element-20647">
              <label htmlFor="scope" className="block text-sm font-medium text-gray-700" data-id="element-20648">
                Scope <span className="text-red-500" data-id="element-20649">*</span>
              </label>
              <div className="mt-1" data-id="element-20650">
                <select id="scope" name="scope" value={formData.scope} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20651">
                  <option value="Global" data-id="element-20652">Global</option>
                  <option value="OrgUnit" data-id="element-20653">Organisation Unit</option>
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-20654">
                {formData.scope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
              </p>
            </div>

            {/* OrgUnit Selector */}
            {formData.scope === 'OrgUnit' && <div className="sm:col-span-3" data-id="element-20655">
                <OrgUnitSelector value={formData.orgUnitId || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} error={errors.orgUnitId} data-id="element-20656" />
              </div>}

            <div className="sm:col-span-4" data-id="element-20657">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-20658">
                Tag Type Name <span className="text-red-500" data-id="element-20659">*</span>
              </label>
              <div className="mt-1" data-id="element-20660">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Priority, Project, Department" data-id="element-20661" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-20662">{errors.name}</p>}
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-20663">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-20664">
                Description
              </label>
              <div className="mt-1" data-id="element-20665">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the tag type" data-id="element-20666" />
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-20667">
              <div className="flex items-start" data-id="element-20668">
                <div className="flex items-center h-5" data-id="element-20669">
                  <input id="isRestricted" name="isRestricted" type="checkbox" checked={formData.isRestricted} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-20670" />
                </div>
                <div className="ml-3 text-sm" data-id="element-20671">
                  <label htmlFor="isRestricted" className="font-medium text-gray-700" data-id="element-20672">
                    Restricted Tag Type
                  </label>
                  <p className="text-gray-500" data-id="element-20673">
                    When checked, only predefined tag values will be allowed for
                    this tag type
                  </p>
                </div>
              </div>
            </div>

            <div className="sm:col-span-6" data-id="element-20674">
              <label className="block text-sm font-medium text-gray-700" data-id="element-20675">
                Color Palette
              </label>
              <p className="mt-1 text-sm text-gray-500" data-id="element-20676">
                Define the colors that can be used for tags of this type
              </p>
              <div className="mt-2" data-id="element-20677">
                <div className="flex flex-wrap gap-2 mb-4" data-id="element-20678">
                  {formData.colourPalette && formData.colourPalette.map((color, index) => <div key={index} className="flex items-center space-x-1 bg-gray-100 rounded-md p-1" data-id="element-20679">
                        <div className="h-6 w-6 rounded-md" style={{
                    backgroundColor: color
                  }} data-id="element-20680" />
                        <span className="text-xs text-gray-700" data-id="element-20681">{color}</span>
                        <button type="button" onClick={() => handleRemoveColor(color)} className="text-gray-400 hover:text-red-500" data-id="element-20682">
                          <TrashIcon className="h-4 w-4" data-id="element-20683" />
                        </button>
                      </div>)}
                </div>
                <div className="flex items-center space-x-2" data-id="element-20684">
                  <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="h-8 w-8 rounded-md border-gray-300" data-id="element-20685" />
                  <input type="text" value={newColor} onChange={e => setNewColor(e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-24 sm:text-sm border-gray-300 rounded-md" placeholder="#RRGGBB" data-id="element-20686" />
                  <button type="button" onClick={handleAddColor} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20687">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-20688" />
                    Add Color
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-20689">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20690">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-20691" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20692">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-20693" />
            {tagType ? 'Update Tag Type' : 'Create Tag Type'}
          </button>
        </div>
      </form>
    </div>;
}